// NPS de un toque en el Home (ítem 9 del BACKLOG, OK del fundador 2026-08-28:
// "en el Home"). Card discreta, jamás modal: una pregunta, 0 a 10 en un toque,
// X para "ahora no" sin insistir (descartar consume la cuota igual que
// responder: anti-culpa). La cuota vive en lib/microEncuestas.js (1 vez al mes
// por usuario, 1 micro encuesta por semana por dispositivo, jamás a mitad de
// turno) y es fail-safe hacia NO molestar: sin storage, no hay card.
// A telemetría viaja SOLO el puntaje y el rol del perfil (catálogo cerrado).
//
// Desde el 2026-10-04 ("aparece siempre", reclamo del fundador con 7
// respuestas suyas en un mes): (1) APARECER ya consume la cuota, no solo
// responder o cerrar, porque quien la ignora la veía en cada entrada al
// inicio; y (2) la cuota sigue a la CUENTA (lib/npsCuenta.js): antes de
// pintar se pregunta si esta cuenta ya la vio en 30 días en cualquier
// aparato, y mientras no se sabe no se pinta nada (cero parpadeo).
import { useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import Card from "../foundational/Card";
import IconButton from "../foundational/IconButton";
import { trackEvent } from "../../lib/eventTracking";
import { cargarRegistros } from "../../lib/registroTurnoStore";
import { novedadesPendientes } from "../../lib/queCambio";
import {
  npsHabilitado, puedeMostrarNps, consumirNps, PUNTAJES_NPS, esPuntajeNpsValido,
} from "../../lib/microEncuestas";
import { npsVistoEnCuenta } from "../../lib/npsCuenta";

// El agradecimiento dura lo justo para leerse y la card se va sola.
export const MS_GRACIAS_NPS = 2500;

export default function NpsCard({ userId, rol, perfilCreadoEn = null, modulo = "home", className = "" }) {
  // 'pregunta' → 'gracias' (tras responder) → 'oculta'; descartar salta a 'oculta'.
  const [estado, setEstado] = useState("pregunta");

  // Se decide UNA vez por usuario al montar (mismo patrón que QueCambioCard):
  // si hay novedades sin leer, el NPS espera a la próxima apertura para que
  // nunca haya dos cards pidiendo atención a la vez.
  const localLibre = useMemo(() => {
    if (!npsHabilitado() || !userId) return false;
    try {
      if (novedadesPendientes(userId).length > 0) return false;
      return puedeMostrarNps({ userId, perfilCreadoEn, registros: cargarRegistros(userId) });
    } catch {
      return false;
    }
  }, [userId, perfilCreadoEn]);

  // 'esperando' → 'libre' | 'vista'. Solo se consulta si este aparato dice
  // que le toca; un error o sin red cuenta como vista (no molestar).
  const [cuenta, setCuenta] = useState("esperando");
  useEffect(() => {
    if (!localLibre) return undefined;
    let vigente = true;
    setCuenta("esperando");
    npsVistoEnCuenta(userId).then((vista) => {
      if (!vigente) return;
      if (vista === false) { setCuenta("libre"); return; }
      // La cuenta ya la vio (o no se sabe): este aparato lo anota para no
      // volver a preguntarle a la red en cada entrada.
      if (vista === true) consumirNps({ userId });
      setCuenta("vista");
    });
    return () => { vigente = false; };
  }, [localLibre, userId]);

  const mostrar = localLibre && cuenta === "libre";

  // Aparecer consume la cuota (aquí y en la cuenta, con el evento). Una vez
  // por montaje: es la aparición real, no un render.
  const yaMarcada = useRef(false);
  useEffect(() => {
    if (!mostrar || yaMarcada.current) return;
    yaMarcada.current = true;
    consumirNps({ userId });
    trackEvent("nps_mostrado", { modulo });
  }, [mostrar, userId, modulo]);

  useEffect(() => {
    if (estado !== "gracias") return undefined;
    const t = setTimeout(() => setEstado("oculta"), MS_GRACIAS_NPS);
    return () => clearTimeout(t);
  }, [estado]);

  if (!mostrar || estado === "oculta") return null;

  const responder = (puntaje) => {
    if (!esPuntajeNpsValido(puntaje)) return;
    consumirNps({ userId });
    // Un evento por respuesta real (patrón #523): se emite en el handler del
    // toque, jamás en un efecto. `rol` viene del perfil (catálogo cerrado).
    trackEvent("nps_respondido", {
      modulo,
      payload: { puntaje, rol: typeof rol === "string" && rol ? rol : "sin_rol" },
    });
    setEstado("gracias");
  };

  const descartar = () => {
    consumirNps({ userId });
    trackEvent("nps_descartado", { modulo });
    setEstado("oculta");
  };

  return (
    <Card as="section" padding="sm" className={className}>
      {estado === "gracias" ? (
        <p className="text-small text-ink-subtle font-sans" role="status">
          Gracias. Tu respuesta ayuda a mejorar MiPAE.
        </p>
      ) : (
        <>
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <p className="text-micro uppercase tracking-wide text-ink-muted font-sans font-semibold">
                Una pregunta rápida
              </p>
              <p className="mt-1 text-small font-semibold text-ink font-sans leading-snug">
                ¿Qué tan probable es que recomiendes MiPAE a un colega o compañero?
              </p>
            </div>
            <IconButton
              icon={<X size={16} strokeWidth={1.6} />}
              ariaLabel="Ahora no, cerrar la pregunta"
              variant="muted"
              onClick={descartar}
              className="-mt-1 -mr-1 flex-shrink-0"
            />
          </div>
          {/* 6 columnas en teléfono (dos filas) para que cada número conserve
              los 44px de toque; en pantalla ancha, la escala entera en una fila. */}
          <div className="mt-3 grid grid-cols-6 md:grid-cols-11 gap-1.5" role="group" aria-label="De 0 a 10">
            {PUNTAJES_NPS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => responder(p)}
                aria-label={`${p} de 10`}
                className="min-h-[44px] rounded-tk-sm border border-edge bg-paper text-small font-semibold text-ink font-sans hover:border-accent-border hover:text-accent transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
              >
                {p}
              </button>
            ))}
          </div>
          <div className="mt-1.5 flex justify-between text-micro text-ink-muted font-sans">
            <span>Nada probable</span>
            <span>Muy probable</span>
          </div>
        </>
      )}
    </Card>
  );
}
